import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import * as FileSaver from 'file-saver-es';

import { JudgeInfo, ApplicationService, NomineeTypeInfo, SchoolNominationInfo } from 'services/application/application.service';
import { UserInfo, UserService } from 'services/user/user.service';
import { AwardProgramInfo, AwardsService } from 'services/awards/awards.service';
import { ErrorService } from 'services/error/error.service';
import { DialogService } from 'services/dialogs/dialogs.service';
import { SplashService } from 'services/splash/splash.service';
import { DownloadService } from 'services/download/download.service';
import { SettingsService } from 'services/settings/settings.service';

@Component({
    selector: 'app-judgetool',
    templateUrl: './judgetool.component.html',
    styleUrls: ['./judgetool.component.scss']
})

export class JudgeToolComponent implements OnInit, OnDestroy {
    private _subrelease: Subject<void> = new Subject<void>();

    displayedColumns: string[] = ['JudgingKey', 'SortableName', 'PhaseName', 'DistrictNumber', 'NomineeTypeName', 'Actions'];
    dataSource: MatTableDataSource<JudgeInfo>;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    User: UserInfo;
    AwardInfo: AwardProgramInfo;
    AllNomTypes: NomineeTypeInfo[];
    NomTypes: NomineeTypeInfo[];
    IsHOF: boolean;
    HofInitialJudgeCount: number;
	HofNomCount: number;
	SchoolYear: string;

    constructor(private appSvc: ApplicationService,
        private awardSvc: AwardsService,
        private errSvc: ErrorService,
        private userSvc: UserService,
        private dlgSvc: DialogService,
        private downloadSvc: DownloadService,
		private splashSvc: SplashService,
		private settingsSvc: SettingsService) { }

    ngOnInit() {
        this.userSvc.UserSubject.pipe(
            takeUntil(this._subrelease)
        ).subscribe(user => {
            this.User = user;
            let award$ = this.awardSvc.CurrentAwardsSub;
			let allNomTypes$ = this.appSvc.GetNomineeTypes();
			let schoolYear$ = this.settingsSvc.CurrentSchoolYearSubject;

			if (user == null) return;

			combineLatest(award$, allNomTypes$, schoolYear$).pipe(
                takeUntil(this._subrelease),
                map(_pck => {
                    return {
                        Award: _pck[0],
						AllNomineeTypes: _pck[1],
						SchoolYear: _pck[2]
                    };
                })
            ).subscribe(packet => {
                this.AwardInfo = packet.Award;
                this.AllNomTypes = packet.AllNomineeTypes;
				this.NomTypes = packet.AllNomineeTypes.filter(f => f.AwardID === this.AwardInfo.AwardID);
				this.SchoolYear = packet.SchoolYear;

                this.IsHOF = this.AwardInfo.ShortName === 'HOF'

				let judges$ = this.appSvc.GetJudges(this.User.AuthToken, this.User.CurrentRole, this.AwardInfo.AwardID, this.SchoolYear);

                judges$.pipe(takeUntil(this._subrelease)).subscribe((judges: JudgeInfo[]) => {
                    this.SetJudges(judges);

					if (this.IsHOF === true) {
						let nominees$ = this.appSvc.GetNominationsByWorkLocation(this.User.AuthToken, 'HALL', this.AwardInfo.AwardID, this.SchoolYear);

                        nominees$.pipe(takeUntil(this._subrelease)).subscribe((nomInfo: SchoolNominationInfo) => {
                            this.HofNomCount = nomInfo.Nominees.length;
                        });
                    }

                }, error => this.errSvc.HandleServiceError(error));
            }, error => this.errSvc.HandleServiceError(error));
        }, error => this.errSvc.HandleServiceError(error));
    }

    AddJudge() {
        this.dlgSvc.OpenJudgeAddDialog(this.NomTypes).pipe(takeUntil(this._subrelease)).subscribe(judge => {
            if (judge != null) {
                this.appSvc.UpsertJudge(
                    this.User.AuthToken,
                    this.User.CurrentRole,
                    this.AwardInfo.AwardID,
                    0,
                    judge.FirstName,
                    judge.LastName,
                    judge.Email,
                    judge.DistrictNumber,
                    judge.NomineeTypeID,
                    judge.JudgingPhase,
					judge.SendEmail,
					this.SchoolYear).pipe(takeUntil(this._subrelease)).subscribe((judges: JudgeInfo[]) => {
                        this.splashSvc.CloseSplash();
                        this.splashSvc.SnackbarSuccess('Judge Added Successfully', 5000);
                        this.SetJudges(judges);
                    }, error =>
                        this.errSvc.HandleServiceError(error)
                );
            }
        });
    }

    EditJudge(oldJudge: JudgeInfo) {
        this.dlgSvc.OpenJudgeAddDialog(this.NomTypes, oldJudge).pipe(takeUntil(this._subrelease)).subscribe(judge => {
            if (judge != null) {
                this.appSvc.UpsertJudge(
                    this.User.AuthToken,
                    this.User.CurrentRole,
                    this.AwardInfo.AwardID,
                    oldJudge.JudgeID,
                    judge.FirstName,
                    judge.LastName,
                    judge.Email,
                    judge.DistrictNumber,
                    judge.NomineeTypeID,
                    judge.JudgingPhase,
					false,
					this.SchoolYear).pipe(takeUntil(this._subrelease)).subscribe((judges: JudgeInfo[]) => {
                        this.splashSvc.CloseSplash();
                        this.splashSvc.SnackbarSuccess('Judge Edited Successfully', 5000);
                        this.SetJudges(judges);
                    }, error =>
                        this.errSvc.HandleServiceError(error)
                    );
            }
        });
    }

    RemoveJudge(judge: JudgeInfo) {
        this.dlgSvc.OpenConfirmationDialog({
            TitleText: 'Confirmation',
            MessageText: 'Are you sure you want to remove this judge and all their scoring?',
            OKText: 'Yes',
            CancelText: 'No'
        }).pipe(takeUntil(this._subrelease)).subscribe(res => {
            if (res === true) {
                this.appSvc.RemoveJudge(this.User.AuthToken, this.User.CurrentRole, judge.JudgeID).pipe(takeUntil(this._subrelease)).subscribe((judges: JudgeInfo[]) => {
                    this.splashSvc.CloseSplash();
                    this.splashSvc.SnackbarSuccess('Judge Removed Successfully', 5000);
                    this.SetJudges(judges);
                }, error => this.errSvc.HandleServiceError(error));
            }
        });
    }

    SetJudges(judges: JudgeInfo[]) {
        this.SetNames(judges);

        if (judges != null && judges.length > 0) {
            this.HofInitialJudgeCount = judges.filter(j => j.JudgingPhase === 1).length;
        }
        else {
            this.HofInitialJudgeCount = 0;
        }

        this.dataSource = new MatTableDataSource(judges);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    SetNames(judges: JudgeInfo[]) {
        for (let j of judges) {
            let potentialNomType = this.AllNomTypes.find(f => f.NomineeTypeID === j.NomineeTypeID);
            if (potentialNomType != null) {
                j.NomineeTypeName = potentialNomType.Description;
            }
            
            switch (j.JudgingPhase) {
                case 1: {
                    j.PhaseName = 'Initial Judging';
                    break;
                }
                case 2: {
                    j.PhaseName = 'Initial Judging Tie-Breaker';
                    break;
                }
                case 3: {
                    j.PhaseName = 'Final Judging';
                    break;
                }
                case 4: {
                    j.PhaseName = 'Final Judging Tie-Breaker';
                    break;
                }
                default: {
                    j.PhaseName = '';
                    break;
                }
            }
        }
    }

    EmailJudges() {
        this.dlgSvc.OpenJudgeEmailDialog(this.NomTypes).pipe(takeUntil(this._subrelease)).subscribe(judge => {
            if (judge != null) {
                this.appSvc.EmailJudges(
                    this.User.AuthToken,
                    this.User.CurrentRole,
                    this.AwardInfo.AwardID,
                    judge.NomineeTypeID,
					judge.JudgingPhase,
					this.SchoolYear).pipe(takeUntil(this._subrelease)).subscribe(res => {
                        this.splashSvc.SnackbarSuccess('Judges Emailed Successfully', 5000);
                    }, error =>
                        this.errSvc.HandleServiceError(error)
                    );
            }
        });
    }

    GetSpreadsheet() {
        this.downloadSvc.DownloadBlankSpreadsheet().pipe(takeUntil(this._subrelease)).subscribe(fileresult => {
            this.splashSvc.CloseSplash();
            FileSaver.saveAs(fileresult.body, 'PAW_JudgeImport_Blank.xlsx');
        }, error =>
            this.errSvc.HandleServiceError(error));
    }

    UploadSpreadsheet() {
        //file upload dialog
        this.dlgSvc.OpenJudgeSpreadsheetDialog().pipe(takeUntil(this._subrelease)).subscribe((file: File) => {
            if (file != null) {
				//upload and validate spreadsheet
				this.appSvc.UploadJudgeSpreadsheet(this.User.AuthToken, this.User.CurrentRole, file, this.SchoolYear, this.AwardInfo.AwardID).pipe(takeUntil(this._subrelease)).subscribe((judges: JudgeInfo[]) => {
                    this.splashSvc.CloseSplash();
                    this.splashSvc.SnackbarSuccess('Judges Read Successfully', 5000);
                    this.SetNames(judges);

                    //show results of read in dialog
                    this.dlgSvc.OpenJudgeConfirmDialog(judges).pipe(takeUntil(this._subrelease)).subscribe((judges: JudgeInfo[]) => {
                        if (judges != null) {
                            for (let j of judges) {
                                j.AwardID = this.AwardInfo.AwardID;
                            }

							//upload valid judges to save to db
							this.appSvc.UpsertJudgeList(this.User.AuthToken, this.User.CurrentRole, judges, this.SchoolYear).pipe(takeUntil(this._subrelease)).subscribe((judgesToUpdateTable: JudgeInfo[]) => {
                                this.splashSvc.CloseSplash();
                                this.splashSvc.SnackbarSuccess('Judges Added Successfully', 5000);
                                this.SetJudges(judgesToUpdateTable);
                            }, error =>
                                this.errSvc.HandleServiceError(error)
                            );
                        }
                    }, error => this.errSvc.HandleServiceError(error));
                }, error => this.errSvc.HandleServiceError(error));
            }
        });
    }

    DivideJudges() {
        this.dlgSvc.OpenJudgeDivideDialog(this.HofInitialJudgeCount, this.HofNomCount).pipe(takeUntil(this._subrelease)).subscribe(res => {
            if (res != null) {
                if (res === 'reset') {
                    //reset to default
                    this.appSvc.ResetJudgeGroups(
                        this.User.AuthToken,
						this.User.CurrentRole,
						this.SchoolYear
                    ).pipe(takeUntil(this._subrelease)).subscribe((judges: JudgeInfo[]) => {
                        this.splashSvc.SnackbarSuccess('Judges Reset Successfully', 5000);
                        this.SetJudges(judges);
                    }, error =>
                        this.errSvc.HandleServiceError(error)
                    );
                }
                else {
                    //Divide judges into districts
                    this.appSvc.DivideJudges(
                        this.User.AuthToken,
                        this.User.CurrentRole,
						res,
						this.SchoolYear
                    ).pipe(takeUntil(this._subrelease)).subscribe((judges: JudgeInfo[]) => {
                        this.splashSvc.SnackbarSuccess('Judges Divided Successfully', 5000);
                        this.SetJudges(judges);
                    }, error =>
                        this.errSvc.HandleServiceError(error)
                    );
                }
            }
        });
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    applyFilter(filterValue: string) {
        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }
}
